// /en/blog/$slug — individual EN expat blog detail with Article/FAQ/Breadcrumb JSON-LD.
import { blogCover } from "@/data/blog-covers";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { BLOG_HEAD_EN } from "@/data/blog-head.en.gen";
import { BlogArticleEn } from "@/components/BlogArticleEn";
import { speakableSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { getAboutEntities } from "@/data/blog-entity-map";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/blog/$slug")({
  loader: async ({ params }) => {
    if (!BLOG_HEAD_EN[params.slug]) throw notFound();
    const { BLOG_BODY_EN } = await import("@/data/blog-body.en.gen");
    return { slug: params.slug, articleBody: BLOG_BODY_EN[params.slug] ?? "" };
  },
  head: ({ params, loaderData }) => {
    const p = BLOG_HEAD_EN[params.slug];
    if (!p) return { meta: [{ title: ctaTitle("Article not found", "/en/blog/$slug", "en")}] };
    const cover = blogCover(p.category, "en");
    const url = `${SITE}/en/blog/${p.slug}`;
    return {
      meta: [
        { title: ctaTitle(p.metaTitle, "/en/blog/$slug", "en")},
        { name: "description", content: ctaDesc(p.metaDesc, "/en/blog/$slug", "en")},
        { name: "keywords", content: p.tags.join(", ") },
        { name: "author", content: p.author },
        { property: "og:title", content: ctaTitle(p.metaTitle, "/en/blog/$slug", "en")},
        { property: "og:description", content: ctaDesc(p.metaDesc, "/en/blog/$slug", "en")},
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "article:published_time", content: p.publishedAt },
        { property: "article:modified_time", content: p.updatedAt },
        { property: "article:author", content: p.author },
        { property: "article:section", content: p.category },
        { property: "og:image", content: cover.absolute },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: cover.altEn },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ctaTitle(p.metaTitle, "/en/blog/$slug", "en")},
        { name: "twitter:description", content: p.metaDesc },
        { name: "twitter:image", content: cover.absolute },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/blog/${p.slug}`, `/en/blog/${p.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "@id": `${url}#article`,
              headline: p.title,
              description: p.metaDesc,
              image: [cover.absolute],
              inLanguage: "en",
              datePublished: p.publishedAt,
              dateModified: p.updatedAt,
              author: {
                "@type": "Person",
                "@id": `${SITE}/about/team#${p.author.replace(/\s+/g, "-").toLowerCase()}`,
                name: p.author,
                url: `${SITE}/en/about/team`,
                worksFor: { "@id": `${SITE}/#organization` },
              },
              publisher: { "@id": `${SITE}/#organization` },
              mainEntityOfPage: { "@type": "WebPage", "@id": url },
              keywords: p.tags.join(", "),
              articleSection: p.category,
              wordCount: p.wordCount,
              articleBody: loaderData?.articleBody ?? "",
              timeRequired: `PT${p.readMinutes}M`,
              isAccessibleForFree: true,
              isPartOf: { "@id": `${SITE}/en/blog#collection` },
              about: getAboutEntities(p.slug, "en"),
              mentions: getAboutEntities(p.slug, "en"),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/en/blog` },
                { "@type": "ListItem", position: 3, name: p.title, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: EnBlogPostPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Article not found</h1>
      <Link to="/en/blog" className="mt-4 inline-block text-amber-300 underline">Back to Blog</Link>
    </main>
  ),
});

function EnBlogPostPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  return <BlogArticleEn slug={slug} />;
}
